<form (ngSubmit)="submit()" class="space-y-4">
  <!-- Shape -->
  <div class="rounded-lg border p-3 text-xs"
    [ngClass]="pointCount >= 3 ? 'border-green-200 bg-green-50 text-green-800' : 'border-blue-200 bg-blue-50 text-blue-800'">
    <i class="fas fa-draw-polygon mr-1"></i>
    <ng-container *ngIf="isNew; else editHint">
      Click on the map to add boundary points ({{ pointCount }} so far, at least 3). The shape closes
      automatically. Drag a white handle to move a point.
    </ng-container>
    <ng-template #editHint>
      Shape: {{ pointCount }} points. Drag the white handles on the map to move a point, or the
      faded midpoint handles to add one.
    </ng-template>
    <div *ngIf="isNew" class="mt-2 flex gap-2">
      <button type="button" (click)="undoPoint.emit()" [disabled]="saving || pointCount === 0"
        class="px-2.5 py-1 rounded-md border border-current bg-white font-semibold disabled:opacity-40">
        <i class="fas fa-undo mr-1"></i>Undo point
      </button>
      <button type="button" (click)="clearPoints.emit()" [disabled]="saving || pointCount === 0"
        class="px-2.5 py-1 rounded-md border border-current bg-white font-semibold disabled:opacity-40">
        <i class="fas fa-eraser mr-1"></i>Clear
      </button>
    </div>
    <p *ngIf="errors['shape']" class="mt-1 font-semibold text-red-600">{{ errors['shape'] }}</p>
  </div>

  <!-- Name -->
  <div>
    <label class="block text-sm font-medium text-gray-700 mb-1">Zone name</label>
    <input type="text" [(ngModel)]="name" name="name" maxlength="100" [disabled]="saving"
      class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
      [class.border-red-400]="errors['name']" [class.border-gray-300]="!errors['name']" />
    <p *ngIf="errors['name']" class="text-xs text-red-600 mt-1">{{ errors['name'] }}</p>
  </div>

  <!-- Capacity -->
  <div class="grid grid-cols-2 gap-2">
    <div>
      <label class="block text-xs font-medium text-gray-700 mb-1">Max per day</label>
      <input type="number" min="1" [(ngModel)]="maxAppointmentsPerDay" name="maxAppointmentsPerDay" [disabled]="saving"
        placeholder="No limit"
        class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none" />
      <p *ngIf="errors['maxAppointmentsPerDay']" class="text-xs text-red-600 mt-1">{{ errors['maxAppointmentsPerDay'] }}</p>
    </div>
    <div>
      <label class="block text-xs font-medium text-gray-700 mb-1">Max per slot</label>
      <input type="number" min="1" [(ngModel)]="maxAppointmentsPerSlot" name="maxAppointmentsPerSlot" [disabled]="saving"
        placeholder="No limit"
        class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none" />
      <p *ngIf="errors['maxAppointmentsPerSlot']" class="text-xs text-red-600 mt-1">{{ errors['maxAppointmentsPerSlot'] }}</p>
    </div>
  </div>

  <!-- Pricing & District -->
  <div>
    <label class="block text-sm font-medium text-gray-700 mb-1">Pricing group</label>
    <select [(ngModel)]="pricingGroupId" name="pricingGroupId" [disabled]="saving"
      class="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-green-500 outline-none">
      <option [ngValue]="null">Global</option>
      <option *ngFor="let group of pricingGroups" [ngValue]="group.id">{{ group.name }}</option>
    </select>
  </div>
  <div>
    <label class="block text-sm font-medium text-gray-700 mb-1">District</label>
    <select [(ngModel)]="districtId" name="districtId" [disabled]="saving"
      class="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-green-500 outline-none">
      <option [ngValue]="null">Unassigned</option>
      <option *ngFor="let district of districts" [ngValue]="district.id">{{ district.name }}</option>
    </select>
  </div>

  <!-- Service schedule -->
  <div class="border-t border-gray-100 pt-3">
    <div class="flex items-center justify-between mb-2">
      <label class="text-sm font-medium text-gray-700">Service days</label>
      <label class="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
        <input type="checkbox" [(ngModel)]="everyDay" name="everyDay" [disabled]="saving" class="accent-green-600" />
        Every day, all slots
      </label>
    </div>

    <div *ngIf="!everyDay" class="space-y-1.5">
      <div *ngFor="let row of days" class="rounded-lg border px-2.5 py-2"
        [ngClass]="row.enabled ? 'border-green-200 bg-green-50/50' : 'border-gray-200 bg-gray-50'">
        <div class="flex items-center justify-between">
          <label class="flex items-center gap-2 text-sm font-medium cursor-pointer"
            [ngClass]="row.enabled ? 'text-gray-800' : 'text-gray-400'">
            <input type="checkbox" [(ngModel)]="row.enabled" [name]="'day-' + row.day" [disabled]="saving"
              class="accent-green-600" />
            {{ row.label }}
          </label>
          <label *ngIf="row.enabled && timeSlots.length > 0" class="flex items-center gap-1 text-[11px] text-gray-500 cursor-pointer">
            <input type="checkbox" [(ngModel)]="row.allSlots" [name]="'all-' + row.day" (ngModelChange)="toggleAllSlots(row)"
              [disabled]="saving" class="accent-green-600" />
            All slots
          </label>
        </div>
        <div *ngIf="row.enabled && !row.allSlots" class="mt-1.5 flex flex-wrap gap-1.5 pl-6">
          <label *ngFor="let slot of timeSlots"
            class="flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-0.5 text-[11px] text-gray-700 cursor-pointer">
            <input type="checkbox" [(ngModel)]="row.slots[slot]" [name]="'slot-' + row.day + '-' + slot" [disabled]="saving"
              class="accent-green-600" />
            {{ slot }}
          </label>
        </div>
      </div>
    </div>
    <p *ngIf="errors['schedule']" class="text-xs text-red-600 mt-1">{{ errors['schedule'] }}</p>
    <p class="text-[11px] text-gray-400 mt-1">
      Customers can only book on the selected days and slots. Use Holidays for one-off closures.
    </p>
  </div>

  <app-booking-conflict-panel *ngIf="conflict" [conflict]="conflict"
    (dismiss)="conflictDismissed.emit()"></app-booking-conflict-panel>

  <div class="flex gap-2 pt-2">
    <button type="button" (click)="cancel.emit()" [disabled]="saving"
      class="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
      Cancel
    </button>
    <button type="submit" [disabled]="saving"
      class="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center font-medium">
      <i *ngIf="saving" class="fas fa-spinner fa-spin mr-2"></i>
      {{ saving ? 'Saving...' : (isNew ? 'Create zone' : 'Save') }}
    </button>
  </div>
</form>
